/* Size guide panel: open and close.

   Scoped entirely to its own markup (snippets/dico-size-guide.liquid). It adds
   no listeners to the product form, the size select or the buy button, because
   this page's add-to-cart path has been broken by side effects before and is not
   worth risking for a size chart.

   The panel and overlay carry the `hidden` attribute in the markup and only lose
   it while open. That is what guarantees the overlay cannot sit invisibly over
   the page intercepting clicks on Add to cart -- a transparent full-screen
   element is exactly the shape of that bug.

   ------------------------------------------------------------------
   Why the panel is moved to <body>

   The snippet renders inside the product column, and the close X came out
   underneath the site header: the header's search and cart icons were drawn on
   top of it, so on a page with the announcement bar dismissed - where the header
   sits at the very top of the window - there was no way to shut the panel except
   Escape or a click on the overlay.

   It was not a z-index number problem. The panel already had z-index 10001
   against a header on 99. But z-index only ranks siblings within a stacking
   context, and one of the panel's ancestors in the product column establishes
   one, so 10001 ranked the panel against the other things inside that column,
   not against the header. The whole column then painted below the header as a
   unit, carrying the panel with it.

   Raising the number could not fix that, and the ancestor that creates the
   context is core theme markup this feature deliberately does not touch. So on
   first run the overlay and panel are appended to <body>, out of the column and
   into the root stacking context, where their z-index does rank against the
   header. It also removes any chance of an ancestor's overflow clipping them.

   The move has one consequence worth knowing about: when the theme re-renders
   the product form it inserts a fresh copy of the whole snippet, so a second
   overlay and panel appear inside the column. panelFor() below keeps the copy
   already on <body> and deletes the new one, which also means the panel's own
   listeners are bound exactly once, for the life of the page.
   ------------------------------------------------------------------

   ------------------------------------------------------------------
   Why the trigger is bound directly, and re-bound after re-renders

   Two other approaches were tried and abandoned, both because on this page a
   click inside the product form does not travel:

     - Delegating from document in the bubble phase. A counter on document
       recorded zero clicks while the trigger was being clicked, so something
       between the button and the root stops propagation on the way up.
     - Delegating from document in the capture phase, which normally beats a
       stopPropagation further down. Also never fired, so the theme is stopping
       the event at document level before this file's listener is reached -
       almost certainly the add-to-cart size gate, which intercepts clicks in
       this form and registers before this deferred script runs.

   So the trigger keeps a listener of its own, where it worked from the start.
   What broke that version was not the binding but its lifetime: the re-render
   throws away the bound button and puts an identical-looking new one in its
   place, and no shopify:section:load fires for that swap. The MutationObserver
   below re-runs the binding on any change inside the product column, and the
   dicoSgBound flag stops duplicate listeners stacking on a button that
   survived. Same pattern as assets/dico-inventory-gate.js.
   ------------------------------------------------------------------ */
(function () {
    'use strict';

    var openRef = null;
    var lastFocus = null;

    function hoist(el) {
        if (el && el.parentNode !== document.body) { document.body.appendChild(el); }
    }

    /* Returns the live element for an id, preferring one already moved to
       <body> and deleting any duplicate the theme has just re-rendered into the
       product column. */
    function pick(id, attr) {
        var all = document.querySelectorAll('[' + attr + ']');
        var hoisted = null;
        var strays = [];
        for (var i = 0; i < all.length; i++) {
            if (all[i].id !== id) { continue; }
            if (all[i].parentNode === document.body) { hoisted = all[i]; } else { strays.push(all[i]); }
        }
        if (hoisted) {
            for (var j = 0; j < strays.length; j++) {
                if (strays[j].parentNode) { strays[j].parentNode.removeChild(strays[j]); }
            }
            return hoisted;
        }
        return strays[0] || null;
    }

    function open(panel, overlay) {
        lastFocus = document.activeElement;
        openRef = { panel: panel, overlay: overlay };

        overlay.hidden = false;
        panel.hidden = false;

        /* Next frame, so the browser has painted the hidden-to-visible change
           before the transform starts -- otherwise it jumps instead of sliding. */
        window.requestAnimationFrame(function () {
            overlay.classList.add('is-open');
            panel.classList.add('is-open');
        });

        var close = panel.querySelector('[data-sg-close]');
        if (close) { close.focus(); }
    }

    function shut() {
        if (!openRef) { return; }
        var panel = openRef.panel;
        var overlay = openRef.overlay;
        openRef = null;

        overlay.classList.remove('is-open');
        panel.classList.remove('is-open');

        /* Wait for the slide-out before hiding, or the panel vanishes
           mid-animation. Guarded on is-open so a re-open inside the 300ms is
           not hidden out from under itself by the old timer. */
        window.setTimeout(function () {
            if (!panel.classList.contains('is-open')) {
                overlay.hidden = true;
                panel.hidden = true;
            }
        }, 300);

        if (lastFocus && lastFocus.focus) { lastFocus.focus(); }
        lastFocus = null;
    }

    /* Bound once per panel, for the life of the page - the panel is the copy on
       <body>, which no re-render replaces. */
    function wire(panel, overlay) {
        if (panel.dicoSgWired) { return; }
        panel.dicoSgWired = true;

        overlay.addEventListener('click', shut);
        var close = panel.querySelector('[data-sg-close]');
        if (close) { close.addEventListener('click', shut); }
    }

    function init(trigger) {
        var id = trigger.getAttribute('aria-controls');
        if (!id) { return; }

        var panel = pick(id, 'data-sg-panel');
        var overlay = pick(id + '-ov', 'data-sg-overlay');
        if (!panel || !overlay) { return; }

        hoist(overlay);
        hoist(panel);
        wire(panel, overlay);

        if (trigger.dicoSgBound) { return; }
        trigger.dicoSgBound = true;

        trigger.addEventListener('click', function (e) {
            e.preventDefault();
            open(panel, overlay);
        });
    }

    function boot() {
        var triggers = document.querySelectorAll('[data-sg-open]');
        for (var i = 0; i < triggers.length; i++) { init(triggers[i]); }
    }

    var pending = null;
    function bootSoon() {
        if (pending) { window.clearTimeout(pending); }
        pending = window.setTimeout(function () { pending = null; boot(); }, 50);
    }

    document.addEventListener('keydown', function (e) {
        if (!openRef) { return; }
        if (e.key === 'Escape' || e.keyCode === 27) { shut(); }
    });

    function start() {
        boot();
        if (window.MutationObserver) {
            var scope = document.getElementById('mainContent') || document.body;
            new MutationObserver(bootSoon).observe(scope, { childList: true, subtree: true });
        }
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', start);
    } else {
        start();
    }

    document.addEventListener('shopify:section:load', bootSoon);
}());